import React, { useState, useCallback, useEffect } from "react";
import styled, { withTheme } from "styled-components";
import {
  DEFAULT_TIMEZONE_VALUE,
  emailRegex,
  FieldsValidation,
  noDateDefinedOrEmpty,
  NotificationType,
} from "../../../modules/constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getCurrentLocale, isRtl } from "../../../i18n/helpers";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import { getTimeFormatTranslation } from "../../../helpers/timeHelper";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 48px;
  gap: 8px;
  @media (max-width: 576px) {
    margin-top: auto;
    padding-top: 2rem;
    .btn {
      width: 100%;
    }
  }
`;

const StyledCheckboxLabel = styled(Label)`
  cursor: pointer;
  margin-left: 5px;
`;

const StyledDivider = styled.hr`
  height: 2px !important;
  width: 80%;
  text-align: center;
  opacity: 0.15;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 15px;
  color: #515151;
  flex: 0 0 90px;
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StyledDividerContainer = styled(StyledContainerHorizontal)`
  justify-content: center;
`;

const StyledTimezoneDropdown = styled(CustomDropdown)`
  min-width: 0;

  button {
    width: 100%;
  }

  span.custom-dropdown-selected-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 576px) {
    .dropdown-menu {
      max-width: ${(props) =>
        props.maxwidth ? `${props.maxwidth}px` : "auto"};

      .dropdown-item {
        width: max-content;
        min-width: 100%;
      }
    }
  }
`;

const ContactInformationForm = (props) => {
  const {
    onSubmit,
    username,
    email,
    phoneCountry,
    phone,
    yearBirth,
    notifications,
    language,
    caregiverParticipantId,
    onClose,
    timezone,
    timeFormat,
    timezonesList,
  } = props;

  const { t } = useTranslation();

  const [formValues, setFormValues] = useState({
    ugnxID: "",
    yearBirth: "",
    email: "",
    phoneCountry: "",
    phoneNumber: "",
    dialCode: "",
    notificationEmail: false,
    notificationPhone: false,
    timezone: "",
  });

  useEffect(() => {
    let tempTimezone = timezone;
    if (
      timezonesList &&
      !timezonesList.find((timezoneObj) => timezoneObj.id === timezone)
    ) {
      tempTimezone = DEFAULT_TIMEZONE_VALUE;
    }

    setFormValues({
      ugnxID: caregiverParticipantId,
      yearBirth:
        yearBirth && yearBirth !== noDateDefinedOrEmpty
          ? moment(yearBirth).format("Y")
          : "-",
      email: email,
      phoneCountry: phoneCountry || "us",
      phoneNumber: phone,
      notificationEmail: notifications?.indexOf(NotificationType.EMAIL) !== -1,
      notificationPhone: notifications?.indexOf(NotificationType.PHONE) !== -1,
      timezone: tempTimezone,
    });
  }, [
    caregiverParticipantId,
    yearBirth,
    email,
    phoneCountry,
    phone,
    notifications,
    timezone,
    timezonesList,
  ]);

  const [formErrors, setFormError] = useState({
    email: false,
    emailMessage: t("errorText.invalid.email"),
    phone: false,
    phoneMessage: t("errorText.invalid.phoneNumber"),
  });

  const isFormValid = useCallback(() => {
    return !(formErrors.email || formErrors.phone);
  }, [formErrors]);

  const updateFormValues = (index, value, country) => {
    formValues[index] = value;
    if (index === "phoneNumber") {
      formValues["phoneCountry"] = country?.countryCode;
      formValues["dialCode"] = country?.dialCode;
    }
    setFormValues({ ...formValues });
    validateForm(index, value);
  };

  const isEmailValid = (email) => {
    if (
      (email &&
        emailRegex.test(email) &&
        email.length <= FieldsValidation.email.size) ||
      (!email && !formValues?.notificationEmail)
    ) {
      return true;
    }
    return false;
  };

  const isPhoneValid = (phone) => {
    if (phone || (!phone && !formValues.notificationPhone)) {
      const onlyNumber = phone?.replace(formValues?.dialCode, "");

      if (
        (onlyNumber &&
          onlyNumber?.length >= FieldsValidation.phone.minSize &&
          onlyNumber?.length < FieldsValidation.phone.size) ||
        (!onlyNumber && !formValues.notificationPhone)
      ) {
        return true;
      }
    }
    return false;
  };

  const validateForm = (index, value) => {
    switch (index) {
      case "notificationEmail":
        setFormError({
          ...formErrors,
          email: !isEmailValid(formValues?.email),
        });
        break;
      case "email":
        setFormError({
          ...formErrors,
          email: !isEmailValid(value),
        });
        break;
      case "phoneNumber":
        setFormError({
          ...formErrors,
          phone: !isPhoneValid(value),
        });
        break;
      case "notificationPhone":
        setFormError({
          ...formErrors,
          phone: !isPhoneValid(formValues?.phoneNumber),
        });
        break;
      default:
        break;
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      let tempNotifications = [];
      if (formValues.notificationEmail) {
        tempNotifications.push(NotificationType.EMAIL);
      }
      if (formValues.notificationPhone) {
        tempNotifications.push(NotificationType.PHONE);
      }

      const formData = {
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
        country: formValues.phoneCountry || "us",
        reasonForChange: "",
        notifications: tempNotifications,
        timezone: formValues.timezone,
      };

      onSubmit(formData);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const getTimezoneValue = (timezoneId) => {
    if (timezoneId) {
      const timezoneValue = timezonesList?.find(
        (timezoneObj) => timezoneObj.id === timezoneId
      );

      return timezoneValue?.value || DEFAULT_TIMEZONE_VALUE;
    }

    return DEFAULT_TIMEZONE_VALUE;
  };

  return (
    <FormContainer data-testid="contact-information-form">
      <LabelledInput
        title={t("appText.participantID")}
        value={formValues?.ugnxID || ""}
        type="text"
        data-testid="ugnx-id"
        readOnly={true}
      />
      <LabelledInput
        className="mt-3"
        title={t("appText.yearOfBirth")}
        value={formValues?.yearBirth || ""}
        type="text"
        data-testid="year-birth-id"
        readOnly={true}
      />
      <LabelledInput
        className="mt-3"
        title={t("appText.timeFormat")}
        value={getTimeFormatTranslation(timeFormat)}
        type="text"
        data-testid="time-format-id"
        readOnly={true}
      />
      <LabelledInput
        className="mt-3"
        title={t("appText.language")}
        value={language || ""}
        type="text"
        data-testid="language-id"
        readOnly={true}
      />
      <StyledDividerContainer className="mt-3">
        <StyledDivider />
      </StyledDividerContainer>
      <LabelledInput
        className="mt-3"
        title={t("appText.caregiverID")}
        value={username || ""}
        type="text"
        data-testid="caregiver-id"
        readOnly={true}
      />
      <div className="mt-4">
        <Label>{t("appText.contactInformation.help")}</Label>
      </div>
      <StyledContainerHorizontal className="mt-3">
        <StyledLabel>{t("appText.timeZone")}</StyledLabel>
        <StyledTimezoneDropdown
          limitheight
          data-testid="timezone-dropdown"
          tabindexprop={7}
          value={getTimezoneValue(formValues?.timezone)}
          options={timezonesList || []}
          align={isRtl() ? "end" : "start"}
          onChange={(timezoneObj) =>
            updateFormValues("timezone", timezoneObj.id)
          }
          maxwidth={window.innerWidth}
        />
      </StyledContainerHorizontal>
      <LabelledInput
        className="mt-3"
        title={t("appText.email")}
        data-testid="email-id"
        value={formValues?.email || ""}
        type="email"
        autoFocus
        hasError={formErrors?.email}
        errorMessage={formErrors?.emailMessage}
        onChange={(e) => updateFormValues("email", e.target.value)}
      />
      <LabelledInput
        className="mt-3"
        title={t("appText.phoneNumber")}
        data-testid="phonenumber-id"
        value={formValues?.phoneNumber || ""}
        country={formValues?.phoneCountry || "us"}
        hasError={formErrors?.phone}
        errorMessage={formErrors?.phoneMessage}
        type="tel"
        isRtl={isRtl()}
        locale={getCurrentLocale()}
        onChange={(value, country) =>
          updateFormValues("phoneNumber", value, country)
        }
      />
      <div className="mt-4">
        <Label>{t("appText.notifications")}</Label>
      </div>
      <span className="mt-2">
        <Checkbox
          data-testid="notification-email-id"
          checked={formValues?.notificationEmail || false}
          onClick={(e) =>
            updateFormValues(
              "notificationEmail",
              !formValues?.notificationEmail
            )
          }
          icon={true}
          square={true}
        />

        <StyledCheckboxLabel
          data-testid="notification-email-label"
          onClick={(e) =>
            updateFormValues(
              "notificationEmail",
              !formValues?.notificationEmail
            )
          }
        >
          {t("appText.email")}
        </StyledCheckboxLabel>
      </span>
      <span className="mt-3">
        <Checkbox
          data-testid="notification-phone-id"
          checked={formValues?.notificationPhone || false}
          onClick={(e) =>
            updateFormValues(
              "notificationPhone",
              !formValues?.notificationPhone
            )
          }
          icon={true}
          square={true}
        />
        <StyledCheckboxLabel
          data-testid="notification-phone-label"
          onClick={(e) =>
            updateFormValues(
              "notificationPhone",
              !formValues?.notificationPhone
            )
          }
        >
          {t("appText.phone")}
        </StyledCheckboxLabel>
      </span>
      <ActionButtonsContainer>
        <SecondaryButton data-testid="cancel-button" onClick={handleClose}>
          {t("appText.cancel")}
        </SecondaryButton>
        <PrimaryButton
          data-testid="submit-button"
          onClick={handleSubmit}
          disabled={!isFormValid()}
        >
          {t("appText.confirm")}
        </PrimaryButton>
      </ActionButtonsContainer>
    </FormContainer>
  );
};

export default withTheme(ContactInformationForm);
